/**
 * Blog post cards converted 1:1 from the Webflow export (blog.html,
 * detail_post.html, detail_author.html). Each variant keeps the original class
 * names and DOM structure so the Webflow CSS lays them out unchanged:
 *
 * - FeaturedBlogCard   → `blog-post-featured-container` (large hero card)
 * - BlackBlogCard      → `blog-black-card-container`
 * - ClassicBlogCard    → `classic-blog-card`
 * - BlogGridCard       → `blog-white-card-container` (More News / Related)
 *
 * Posts link to /blog/[slug] and authors to /author/[slug]. The grid-wide
 * `fade-in-move-on-scroll` / `.product-card` reveals are handled globally.
 */
import type { FC } from 'react'
import type { Post } from '@/types'

import Link from 'next/link'

interface BlogCardProps {
    post: Post
}

export const FeaturedBlogCard: FC<BlogCardProps> = ({ post }) => (
    <div className='blog-post-featured-container'>
        <Link href={`/blog/${post.slug}`} className='blog-post-featured-main-image-container w-inline-block'>
            <img alt={post.name} loading='lazy' src={post.mainImage} className='blog-post-featured-main-image' />
        </Link>
        <div className='blog-post-featured-details-container'>
            <Link href={`/blog/${post.slug}`} className='w-inline-block'>
                <div className='category-and-time-container'>
                    <div className='blog-featured-category-text'>{post.category}</div>
                    <div className='blog-featured-category-text-grey'>•</div>
                    <div className='blog-featured-category-text-grey'>{post.readTime}</div>
                    <div className='blog-featured-category-text-grey'>mins read</div>
                </div>
                <h2>{post.name}</h2>
            </Link>
            <div className='featured-author-container'>
                <div className='author-container'>
                    <Link href={post.author ? `/author/${post.author.slug}` : '#'} className='author-profile-picture-holder w-inline-block'>
                        <div className='author-profile-picture-container'>
                            <img alt={post.author?.name ?? ''} loading='lazy' src={post.author?.picture ?? ''} className='author-profile-picture-image' />
                        </div>
                        <div className='author-details-holder'>
                            <div className='author-name'>{post.author?.name}</div>
                            <p className='paragraph-s grey-text'>{post.author?.role}</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </div>
)

export const BlackBlogCard: FC<BlogCardProps> = ({ post }) => (
    <div className='blog-black-card-container'>
        <Link href={`/blog/${post.slug}`} className='blog-black-card-thumbnail-holder w-inline-block'>
            <img alt={post.name} loading='lazy' src={post.thumbnail} className='blog-post-featured-main-image' />
        </Link>
        <div className='blog-post-featured-details-container classic'>
            <Link href={`/blog/${post.slug}`} className='w-inline-block'>
                <div className='category-and-time-container'>
                    <div className='blog-featured-category-text'>{post.category}</div>
                    <div className='blog-featured-category-text-grey'>•</div>
                    <div className='blog-featured-category-text-grey'>{post.readTime}</div>
                    <div className='blog-featured-category-text-grey'>mins read</div>
                </div>
                <h3 className='white-text'>{post.name}</h3>
            </Link>
            <div className='featured-author-container small'>
                <div className='author-container'>
                    <Link href={post.author ? `/author/${post.author.slug}` : '#'} className='author-profile-picture-holder w-inline-block'>
                        <div className='author-profile-picture-container'>
                            <img alt={post.author?.name ?? ''} loading='lazy' src={post.author?.picture ?? ''} className='author-profile-picture-image' />
                        </div>
                        <div className='author-details-holder'>
                            <div className='author-name white-text'>{post.author?.name}</div>
                            <p className='paragraph-s grey-text'>{post.author?.role}</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </div>
)

export const ClassicBlogCard: FC<BlogCardProps> = ({ post }) => (
    <div role='listitem' className='classic-blog-item w-dyn-item'>
        <div className='classic-blog-card'>
            <Link href={`/blog/${post.slug}`} className='classic-blog-thumbnail-holder w-inline-block'>
                <img alt={post.name} loading='lazy' src={post.thumbnail} className='blog-post-featured-main-image' />
            </Link>
            <div className='blog-post-featured-details-container classic'>
                <Link href={`/blog/${post.slug}`} className='w-inline-block'>
                    <div className='category-and-time-container'>
                        <div className='blog-featured-category-text'>{post.category}</div>
                        <div className='blog-featured-category-text-grey'>•</div>
                        <div className='blog-featured-category-text-grey'>{post.readTime}</div>
                        <div className='blog-featured-category-text-grey'>mins read</div>
                    </div>
                    <h5 className='blog-title'>{post.name}</h5>
                </Link>
                <div className='featured-author-container classic'>
                    <div className='author-container'>
                        <Link href={post.author ? `/author/${post.author.slug}` : '#'} className='author-profile-picture-holder w-inline-block'>
                            <div className='author-profile-picture-container'>
                                <img alt={post.author?.name ?? ''} loading='lazy' src={post.author?.picture ?? ''} className='author-profile-picture-image' />
                            </div>
                            <div className='author-details-holder'>
                                <div className='author-name'>{post.author?.name}</div>
                                <p className='paragraph-s grey-text'>{post.author?.role}</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
)

export const BlogGridCard: FC<BlogCardProps> = ({ post }) => (
    <div role='listitem' className='blog-grid-item w-dyn-item'>
        <div className='blog-white-card-container'>
            <Link href={`/blog/${post.slug}`} className='blog-black-card-thumbnail-holder small w-inline-block'>
                <img alt={post.name} loading='lazy' src={post.thumbnail} className='blog-post-featured-main-image' />
            </Link>
            <div className='blog-post-featured-details-container classic'>
                <Link href={`/blog/${post.slug}`} className='w-inline-block'>
                    <div className='category-and-time-container'>
                        <div className='blog-featured-category-text'>{post.category}</div>
                        <div className='blog-featured-category-text-grey'>•</div>
                        <div className='blog-featured-category-text-grey'>{post.readTime}</div>
                        <div className='blog-featured-category-text-grey'>mins read</div>
                    </div>
                    <h6>{post.name}</h6>
                </Link>
                <div className='featured-author-container small'>
                    <div className='author-container'>
                        <Link href={post.author ? `/author/${post.author.slug}` : '#'} className='author-profile-picture-holder w-inline-block'>
                            <div className='author-profile-picture-container'>
                                <img alt={post.author?.name ?? ''} loading='lazy' src={post.author?.picture ?? ''} className='author-profile-picture-image' />
                            </div>
                            <div className='author-details-holder'>
                                <div className='author-name'>{post.author?.name}</div>
                                <p className='paragraph-s grey-text'>{post.author?.role}</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
)
